import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router'

import { createRepository } from '../../../infrastructure/storage'
import { AppPath } from '../../AppPath'

interface ImpersonateContextObj {
  impersonate: string | null
  setImpersonate: (impersonate: string) => void
}

const ImpersonateContext = createContext<ImpersonateContextObj | undefined>(undefined)

export interface ImpersonateProviderProps extends PropsWithChildren {
  impersonate: string | null
}

const repository = createRepository<string>('impersonate', 'session')

export const ImpersonateProvider: FunctionComponent<ImpersonateProviderProps> = ({
  children,
  impersonate: propImpersonate,
}) => {
  const navigate = useNavigate()
  const [internalImpersonate, setInternalImpersonate] = useState(() => repository.get())

  if (propImpersonate && propImpersonate !== internalImpersonate) {
    repository.save(propImpersonate)
    setInternalImpersonate(propImpersonate)
  }

  const value = useMemo<ImpersonateContextObj>(
    () => ({
      impersonate: internalImpersonate,
      setImpersonate: (value) => {
        repository.save(value)
        setInternalImpersonate(value)
        navigate(AppPath.HOME, { replace: true })
      },
    }),
    [internalImpersonate, navigate],
  )

  return (
    <ImpersonateContext.Provider value={value}>{children}</ImpersonateContext.Provider>
  )
}

export const useImpersonate = (): ImpersonateContextObj => {
  const context = useContext(ImpersonateContext)
  if (!context) {
    throw new Error('Cannot use ImpersonateContext when undefined.')
  }
  return context
}
